/**
 * Design tokens for the V2 ("Zoho-inspired") UI.
 *
 * Shared on purpose: these started out inside DimensionsRevampV2 while the
 * header kept its own raw px values, and the two immediately drifted — the
 * module ended up on 8/12px radii while the header sat on 6/8px, which is
 * exactly the inconsistency this file exists to prevent. Both
 * `ModalOverViewPageHeaderRevamp` and `DimensionsRevampV2` import from here;
 * add nothing to either that could have come from a token.
 *
 * The page was built with hex values inlined at every call site, so the same
 * conceptual colour drifted between files (#D9D9D9 vs #E8E9EB vs #C4B5FD for
 * "a border", 9px vs 12px vs 14px for "a corner"). That inconsistency is most
 * of what made the UI read as unfinished rather than any single wrong value.
 *
 * The organising rule — the thing that makes a data-dense tool look like a
 * professional SaaS product rather than a demo — is that **structure is
 * greyscale and the accent is spent only where the user can act**. Chrome
 * (surfaces, borders, dividers, idle icons) uses `neutral`; anything that
 * signals interaction (selected, active, focused, primary action) uses
 * `accent`. Reach for `accent` in a border or background only when that
 * element is genuinely in an active state.
 */

/** Surfaces and structure. Nothing here should ever carry brand colour. */
export const neutral = {
  /** Page background behind cards/panels. */
  canvas: '#F7F8FA',
  /** Cards, panels, table body, header bar. */
  surface: '#FFFFFF',
  /** Table header row, toolbar wells, hover rows. */
  surfaceMuted: '#FAFAFA',
  /** Hover state on neutral controls. */
  surfaceHover: '#F2F3F5',
  /**
   * One border colour for everything — gridlines, control outlines, card
   * edges, dividers. An earlier pass used three tints (#EBEDF0/#E3E5E9/
   * #D5D8DE) to imply hierarchy, but at 1px none of them carried enough
   * contrast against white to read as a deliberate line; the UI just looked
   * washed out. A single, slightly darker value gives every edge the same
   * weight, which is what actually reads as tidy.
   *
   * `borderSubtle` is kept as an alias so call sites that mean "hairline"
   * still say so, but it resolves to the same colour on purpose.
   *
   * Note on the value: the first attempt at "one darker border" landed on
   * #D0D5DD, which is only ~9 levels off the #D9D9D9 that several controls
   * (the nav bar, the header chrome) already used — so those simply looked
   * unchanged. #D1D6DD is far enough from every colour it replaced to read as
   * a deliberate line on white.
   */
  borderSubtle: '#D1D6DD',
  border: '#D1D6DD',
  /** Hover only — a state change, not a second border colour in the resting UI. */
  borderStrong: '#AEB6C2',
} as const;

/** Text ramp. Three levels is enough; more just muddies hierarchy. */
export const text = {
  /** Record data, headings — the thing being read. */
  primary: '#1F2430',
  /** Column labels, helper text, secondary metadata. */
  secondary: '#5B6472',
  /** Placeholders and disabled copy. */
  muted: '#98A0AD',
  /** Copy on top of `accent.solid` / dark primary buttons. */
  inverse: '#FFFFFF',
} as const;

/** Brand accent — interaction only. See the note at the top of this file. */
export const accent = {
  solid: '#8B5CF6',
  hover: '#7C4EE0',
  /** Tint for active/selected backgrounds. */
  subtle: '#F5F0FF',
  /** Border for an element in an active/selected state. */
  border: '#C4B5FD',
} as const;

/** Near-black primary action (Linear/Stripe-style), not the purple accent. */
export const primaryAction = {
  bg: '#121826',
  hover: '#2A3441',
} as const;

/**
 * Corner radii — two steps, nothing else.
 *
 * The page originally mixed 4/5/6/7/8/9/10/11/12/14/16/25px with no rule, and
 * that scatter (not any one value) is what made it look unfinished. An earlier
 * pass overcorrected to 4/6px, which read as boxy. 6/8px keeps the "tool" feel
 * — Zoho, Linear and Airtable all sit in this range — without the hard edges.
 *
 * Pick by what the element *is*, not by how big it looks: `control` for
 * anything the user clicks or types into, `surface`/`overlay` for the things
 * that contain them. Nesting a control inside a surface then reads correctly,
 * since the inner radius is always the smaller of the two.
 */
export const radius = {
  /** Buttons, inputs, chips, menu rows. */
  control: '8px',
  /** Cards, panels, modals, the table shell. */
  surface: '12px',
  /** Popovers and menus. */
  overlay: '12px',
} as const;

/** Standard control height — keeps every toolbar item on one optical line. */
export const controlHeight = '32px';

/**
 * Spacing scale. The page was built straight from Figma measurements, so it
 * carried 18 distinct values (7, 9, 11, 13, 14, 18, 21…) with no relationship
 * between them. Nothing lined up, and the eye reads that as sloppiness even
 * when it can't name why. Everything now snaps to this scale — a 4px rhythm
 * with 2/6 available for tight cases.
 *
 * Use `xs`/`sm` for gaps inside a control, `md` for gaps between controls,
 * `lg` for padding inside a card, `xl`+ for separating regions.
 */
export const space = {
  '2xs': '2px',
  xs: '4px',
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  '3xl': '24px',
} as const;

export const font = {
  /** Everything in the UI. */
  body: "'Roboto Regular', Roboto, sans-serif",
  /** Page/section titles only. */
  display: "'Comfortaa', sans-serif",
} as const;

export const fontSize = {
  /** Column headers, badges, helper text. */
  label: '12px',
  /** Default UI + table data size. */
  body: '13px',
  /** Section titles inside panels. */
  subtitle: '15px',
  /** Page title. */
  title: '20px',
} as const;

/** Soft elevation for overlays. Heavy shadows date a UI faster than anything. */
export const shadow = {
  overlay: '0 6px 24px rgba(16, 20, 30, 0.12), 0 1px 3px rgba(16, 20, 30, 0.08)',
  panel: '0 1px 2px rgba(16, 20, 30, 0.04)',
} as const;
